const renderText = (text, ctx) => {
  let out = text || "";
  Object.entries(ctx || {}).forEach(([k, v]) => {
    out = out.split(`{${k}}`).join(String(v));
  });
  return out;
};

const KVTable = ({ rows, ctx }) => (
  <table className="w-full border-collapse text-sm">
    <tbody>
      {rows.map((r, i) => (
        <tr key={i}>
          <td className="border border-neutral-300 bg-neutral-100 px-3 py-2 font-bold w-32 sm:w-40 align-top">{r.label}</td>
          <td className="border border-neutral-300 px-3 py-2 align-top whitespace-pre-wrap">{renderText(r.text, ctx)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export function ContractDocument({ template, ctx, signer, signatureB64, photoB64, idFrontB64, idBackB64, sealB64, showEvidence = true }) {
  if (!template || !ctx) return null;
  const t = template;
  const signed = !!signer;

  return (
    <div className="bg-white text-neutral-900 rounded-lg shadow-2xl p-6 sm:p-10 font-jp leading-relaxed" data-testid="contract-document">
      <h1 className="text-center text-lg sm:text-xl font-bold whitespace-pre-line">{t.title}</h1>
      <p className="text-center text-[10px] tracking-widest text-neutral-500 mt-1 mb-8">{t.subtitle}</p>

      <KVTable rows={t.header_rows} ctx={ctx} />

      <p className="mt-6 text-sm">{renderText(t.preamble, ctx)}</p>

      {t.articles.map((a, i) => (
        <section key={i} className="mt-5">
          <h2 className="text-sm font-bold">{a.heading}</h2>
          {a.paras.map((p, j) => (
            <p key={j} className="mt-1.5 text-sm">{p}</p>
          ))}
        </section>
      ))}

      <p className="mt-8 text-sm">{t.closing}</p>

      <table className="w-full border-collapse text-sm mt-6">
        <tbody>
          <tr>
            <td className="border border-neutral-300 bg-neutral-100 px-3 py-3 font-bold w-12 align-top">{t.party_block.a_label}</td>
            <td className="border border-neutral-300 px-3 py-3 align-top">
              <p>{t.party_block.a_address_label}{signer?.address || ""}</p>
              <p className="mt-1 flex items-center flex-wrap">{t.party_block.a_company_label}{ctx.client_company}
                {signed && sealB64 && <img src={sealB64} alt="印影" className="h-14 w-14 -ml-1 -my-2 object-contain" data-testid="party-seal-a" />}
              </p>
              {signed && signer.phone && <p className="mt-1">電話番号：{signer.phone}</p>}
              <p className="mt-1">
                {t.party_block.a_rep_label}
                {signed && <>{signer.title && <>{signer.title}　</>}{signer.rep_name}</>}
              </p>
              {signed && signatureB64 && (
                <div className="mt-2">
                  <p className="text-xs text-neutral-500">（署名）</p>
                  <img src={signatureB64} alt="署名" className="h-20 rounded border border-neutral-200 bg-white" />
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td className="border border-neutral-300 bg-neutral-100 px-3 py-3 font-bold align-top">{t.party_block.b_label}</td>
            <td className="border border-neutral-300 px-3 py-3 align-top">
              <p>{t.party_block.b_address}</p>
              <p className="mt-1 flex items-center">{t.party_block.b_company}<img src="/prc-seal.png" alt="社印" className="h-16 w-16 -ml-2 -my-3 object-contain" data-testid="party-seal" /></p>
              <p className="mt-1">代表者：{t.party_block.b_rep}</p>
            </td>
          </tr>
        </tbody>
      </table>

      <hr className="my-10 border-neutral-300" />

      <h2 className="text-center font-bold">{t.annex1_title}</h2>
      <p className="text-xs text-neutral-500 mt-1 mb-4">{t.annex1_note}</p>
      <KVTable rows={t.annex1_rows} ctx={ctx} />

      <h2 className="text-center font-bold mt-10">{t.annex2_title}</h2>
      <p className="text-xs text-neutral-500 mt-1 mb-4">{t.annex2_note}</p>
      <KVTable rows={t.annex2_rows} ctx={ctx} />

      <div className="mt-6">
        <p className="text-xs font-bold">{t.ops_notes_title}</p>
        {t.ops_notes.map((n, i) => (
          <p key={i} className="text-xs text-neutral-600 mt-1">{n}</p>
        ))}
      </div>

      {signed && showEvidence && (
        <div className="mt-10 border-t border-neutral-300 pt-6" data-testid="contract-evidence">
          <p className="text-xs font-bold mb-3">【電子契約 締結記録】</p>
          <table className="w-full border-collapse text-xs">
            <tbody>
              {[
                ["署名者（甲・代表者）", [signer.title, signer.rep_name].filter(Boolean).join("　")],
                ["署名者住所", signer.address],
                signer.phone ? ["会社の電話番号", signer.phone] : null,
                ["締結日時", signer.signed_at_jp || signer.signed_at],
                signer.ip ? ["署名時IPアドレス", signer.ip] : null,
              ].filter(Boolean).map(([l, v], i) => (
                <tr key={i}>
                  <td className="border border-neutral-300 bg-neutral-100 px-3 py-1.5 font-bold w-36">{l}</td>
                  <td className="border border-neutral-300 px-3 py-1.5">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(photoB64 || idFrontB64 || idBackB64) && (
            <div className="mt-3 flex flex-wrap gap-5">
              {photoB64 && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">署名者写真</p>
                  <img src={photoB64} alt="署名者写真" className="w-32 rounded border border-neutral-200" />
                </div>
              )}
              {idFrontB64 && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">身分証明書（表面）</p>
                  <img src={idFrontB64} alt="身分証明書 表面" className="w-48 rounded border border-neutral-200" data-testid="evidence-id-front" />
                </div>
              )}
              {idBackB64 && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">身分証明書（裏面）</p>
                  <img src={idBackB64} alt="身分証明書 裏面" className="w-48 rounded border border-neutral-200" data-testid="evidence-id-back" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
